import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Col, FormLabel, Modal, Row, Spinner } from "react-bootstrap";
import ListPage from "../../../layouts/page/ListPage";
import PageTitle from "../../../layouts/PageTitle";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import ListMetricCard, { ListMetricCards } from "../../../components/Common/ListParts/ListMetricCard";
import InputText from "../../../components/Forms/Element/InputText";
import CopyButton from "../../../components/Common/CopyButton";
import StatusFilterSelect, { DEFAULT_STATUS, buildStatusFilter } from "../../../components/Common/Filter/StatusFilterSelect";
import { ApiError } from "../../../components/Common/ApiError";
import { makeColumns } from "./Columns";
import { warnAlertYesOrNo } from "../../../components/alert/defaultAlert";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import axiosInstance from "../../../../services/AxiosInstance";
import { SearchOperation } from "../../../../utils/ApiUtils";
import { clubPublicUrl } from "../../../../utils/ClubUtils";
import "./ClubPartner.css";

/**
 * Parceiros do Clube. Os cartões somam todos os parceiros ativos (o resultado do clube como um
 * todo), e a grade respeita o filtro, como nas demais listagens.
 */
const ClubPartnerList = () => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);
    const [filters, setFilters] = useState([]);

    const [metrics, setMetrics] = useState({ partners: 0, offers: 0, generated: 0, used: 0 });
    const [loadingMetrics, setLoadingMetrics] = useState(false);
    const [clubUrl, setClubUrl] = useState(null);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [access, setAccess] = useState(null);
    const [accessLoading, setAccessLoading] = useState(false);
    const [accessError, setAccessError] = useState('');
    const [gridRef, setGridRef] = useState(null);

    const openAccess = useCallback((partner) => {
        setSelectedPartner(partner);
        setAccess(null);
        setAccessError('');
        setAccessLoading(true);
        axiosInstance.get(`${Api.subscription.club_partner}/${partner.id}/access`)
            .then(({ data }) => setAccess(data))
            .catch(() => setAccessError('Não foi possível consultar o acesso. Tente novamente.'))
            .finally(() => setAccessLoading(false));
    }, []);
    const columns = useMemo(() => makeColumns(openAccess), [openAccess]);
    const generateAccess = () => {
        const generate = () => {
            setAccessLoading(true);
            setAccessError('');
            axiosInstance.post(`${Api.subscription.club_partner}/${selectedPartner.id}/access`)
                .then(({ data }) => {
                    setAccess(data);
                    setSelectedPartner((partner) => ({ ...partner, accessConfigured: true }));
                    gridRef?.current?.api?.refreshInfiniteCache();
                })
                .catch(() => setAccessError('Não foi possível gerar o acesso. Tente novamente.'))
                .finally(() => setAccessLoading(false));
        };
        if (selectedPartner.accessConfigured) {
            warnAlertYesOrNo('Gerar um novo acesso troca o link e o PIN e encerra as sessões atuais do parceiro. Continuar?', generate);
        } else generate();
    };
    const accessUrl = access?.accessPath ? `${window.location.origin}${access.accessPath}` : '';
    const accessMessage = access?.pin ? `Olá! Para validar os cupons do Clube, abra ${accessUrl} e entre com o PIN ${access.pin}.` : '';

    // O link que o gestor divulga aos assinantes.
    useEffect(() => {
        axiosInstance.get(Api.registration.company_info)
            .then(({ data }) => setClubUrl(clubPublicUrl(data)))
            .catch(() => setClubUrl(null));
    }, []);

    // Os cartões vêm de uma consulta própria: a grade pagina e não entrega os registros à tela.
    const loadMetrics = useCallback(() => {
        setLoadingMetrics(true);
        axiosInstance.post(`${Api.subscription.club_partner}/findByFilters`, { filters: [buildStatusFilter(DEFAULT_STATUS)] })
            .then(({ data }) => {
                const rows = Array.isArray(data) ? data : [];
                setMetrics({
                    partners: rows.length,
                    offers: rows.reduce((sum, partner) => sum + (partner.offers || []).filter((offer) => offer.status === 'ACTIVE').length, 0),
                    generated: rows.reduce((sum, partner) => sum + (partner.couponsGenerated || 0), 0),
                    used: rows.reduce((sum, partner) => sum + (partner.couponsUsed || 0), 0)
                });
            })
            .catch(ApiError)
            .finally(() => setLoadingMetrics(false));
    }, []);

    useEffect(() => {
        loadMetrics();
    }, [loadMetrics]);

    const handleSearch = () => {
        const next = [];
        if (name) {
            next.push({ field: "name", value: name, operation: SearchOperation.MATCH });
        }
        next.push(buildStatusFilter(statusFilter));
        setFilters(next);
    };

    const handleClear = () => {
        setName("");
        setStatusFilter(DEFAULT_STATUS);
        setFilters([]);
    };

    const usageRate = metrics.generated > 0 ? `${Math.round((metrics.used / metrics.generated) * 100)}% dos cupons gerados` : "Nenhum cupom gerado";

    return (
        <>
            <PageTitle activeMenu={t("club_partners")} motherMenu={t("subscriptions")} />

            <FilterAccordion onSearch={handleSearch} onClear={handleClear}>
                <Row className="g-3">
                    <Col md={4}>
                        <FormLabel>{t("name")}</FormLabel>
                        <InputText value={name} onChange={(e) => setName(e.target.value)} />
                    </Col>
                    <Col md={3}>
                        <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    </Col>
                </Row>
            </FilterAccordion>

            <ListMetricCards columns={4}>
                <ListMetricCard accent="primary" label="Parceiros ativos" value={metrics.partners}
                    icon="fa-solid fa-handshake" loading={loadingMetrics} />
                <ListMetricCard accent="info" label="Ofertas no ar" value={metrics.offers}
                    icon="fa-solid fa-tags" loading={loadingMetrics} />
                <ListMetricCard accent="warning" label="Cupons gerados" value={metrics.generated}
                    icon="fa-solid fa-ticket" loading={loadingMetrics} />
                <ListMetricCard accent="success" label="Cupons usados" value={metrics.used}
                    icon="fa-solid fa-circle-check" loading={loadingMetrics} caption={usageRate} />
            </ListMetricCards>

            {clubUrl && (
                <Alert variant="info" className="py-2">
                    <div className="club-link-box">
                        <span><i className="fa-solid fa-link me-2"></i>Link dos parceiros no agendamento:</span>
                        <span className="club-link-box__url">{clubUrl}</span>
                        <CopyButton textToCopy={clubUrl} />
                    </div>
                </Alert>
            )}

            <div className="club-partners-grid">
            <ListPage
                showPageTitle={false}
                activeMenu={t("club_partners")}
                motherMenu={t("subscriptions")}
                path={Api.subscription.club_partner}
                route={MenuConfig.subscription.club_partner.route}
                columns={columns}
                rowHeight={60}
                setGridRef={setGridRef}
                filters={filters}
                // A situação já é escolhida no filtro.
                hideInactiveToggle
                onDelete={loadMetrics}
            />
            </div>
            <Modal show={Boolean(selectedPartner)} onHide={() => setSelectedPartner(null)} centered>
                <Modal.Header closeButton><Modal.Title>Acesso · {selectedPartner?.name}</Modal.Title></Modal.Header>
                <Modal.Body>
                    {accessLoading ? <div className="text-center p-4"><Spinner animation="border" /></div> : <>
                        {accessError && <Alert variant="danger">{accessError}</Alert>}
                        {accessUrl && <><p className="mb-1">Link de validação</p><div className="club-link-box mb-3"><span className="club-link-box__url">{accessUrl}</span><CopyButton textToCopy={accessUrl} showText={false} /></div></>}
                        {access?.pin ? <><p className="mb-1">PIN</p><div className="club-access__pin">{access.pin}</div></> : !accessError && <Alert variant="info">{selectedPartner?.accessConfigured ? 'Este PIN antigo não pode ser recuperado. Gere um novo acesso para exibi-lo novamente.' : 'Este parceiro ainda não tem PIN. Gere um acesso para compartilhar.'}</Alert>}
                    </>}
                </Modal.Body>
                <Modal.Footer>
                    {accessMessage && <CopyButton textToCopy={accessMessage} label="Copiar link e PIN" />}
                    <Button variant="outline-primary" onClick={generateAccess} disabled={accessLoading}>{selectedPartner?.accessConfigured ? 'Gerar novo acesso' : 'Gerar acesso'}</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ClubPartnerList;
